import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ISearchableField, ISearchableFields} from '../../../../shared/shared-models/type-sense/default-searchable-fields';
import {setSearchableFields, setStockItemFilters} from '../../store/stock-manager.actions';
import {Store} from '@ngrx/store';
import {getDeepCopyOfObject, isNonEmptyObject} from '../../../../shared/shared-utils/object/object.utils';
import {IFacetCounts} from '../../../../shared/shared-models/type-sense/type-sense-types';
import {FuzzySort, ObjResult} from '../../../../shared-utilities/functions-old/fuzzysort';
import {IDepartment, ISubDepartment} from '../../../../shared/shared-models/stock/departments';
import {IAdvancedFilterGroup} from '../../../settings-new/models/general/advanced-filter-group.interface';
import {
  applyAdvancedFilterGroupFilterToGrid,
  removeAdvancedFilterGroupFilterFromGrid,
  setSelectedAdvancedFilterGroup,
} from '../../../core/store-shared-filter/shared-filter.actions';

import {TLineColour} from '../../../settings-new/models';
import {
  isHideDisabledRefinement,
  STOCK_REFINEMENT_HIDE_DISABLED_FIELD,
} from '../../../../shared/shared-services/type-sense/type-sense.service.utils';

const NO_LINE_COLOUR_KEY = '0';
const NO_LINE_COLOUR_LABEL = 'No line colour';

@Component({
  selector: 'app-stock-refinements',
  templateUrl: './stock-refinements.component.html',
  styleUrls: ['./stock-refinements.component.scss'],
  standalone: false,
})
export class StockRefinementsComponent implements OnInit, OnChanges {

  @Input() facetCounts: IFacetCounts = {};
  @Input() selectedSearchableFields: ISearchableFields = {};
  @Input() selectedDepartmentsAndSubDepartments: IDepartment[] = [];
  @Input() selectedSuppliers: ISearchableFields = {};
  @Input() advancedFilters: IAdvancedFilterGroup[];
  @Input() lineColours: TLineColour;
  @Input() isStockManager = true;
  /** When true, the line-colour facet accordion is hidden (multi-store grid). */
  @Input() hideLineColours = false;

  searchableFields: ISearchableFields = {};
  suppliers: ISearchableFields;
  facets: IFacetCounts;
  departments: ISearchableFields;
  subDepartments: ISearchableFields;
  tags: ISearchableFields;
  fuzzySuppliers: FuzzySort;
  fuzzyDepartments: FuzzySort;
  fuzzySubDepartments: FuzzySort;
  fuzzyMultipleSuppliers: FuzzySort;
  lineColourCopy: TLineColour;
  searchSuppliers = '';
  searchMultipleSuppliers = '';
  searchDepartments = '';
  searchSubDepartments = '';
  selectedFilterName: string;
  filteredSuppliers: ISearchableFields;
  filteredMultipleSuppliers: ISearchableFields;
  searchableDepartments: ISearchableFields;
  filteredDepartments: ISearchableFields;
  searchableSubDepartments: ISearchableFields;
  filteredSubDepartments: ISearchableFields;

  openAccordion: string;
  chips: ISearchableFields;
  hideDisabledItems = false;
  selectedAdvancedFilter: IAdvancedFilterGroup;
  private storedRefinementFilters: Record<string, ISearchableFields> = {};
  lineColourCodes: ISearchableFields;
  multipleSuppliers: ISearchableFields;
  @Output() readonly emitSearchableFields = new EventEmitter<ISearchableFields>();
  @Output() readonly emitFilters = new EventEmitter<Record<string, ISearchableFields>>();
  @Output() readonly emitFilterGroup = new EventEmitter<{ advancedFilterGroup: IAdvancedFilterGroup, type: string }>();
  protected readonly object = Object;
  protected readonly isNonEmptyObject = isNonEmptyObject;

  constructor(
    private readonly store: Store,
  ) {
  }


  @Input() set appliedAdvancedFilters(appliedFilter: IAdvancedFilterGroup) {
    this.selectedFilterName = appliedFilter?.name;
    this.selectedAdvancedFilter = appliedFilter ?? null;
  }

  @Input() set appliedRefinementFilters(filters: Record<string, ISearchableFields> | null | undefined) {
    this.storedRefinementFilters = filters ?? {};
  }

  ngOnInit(): void {
    this.initializeData();
    this.restoreAppliedRefinementFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initializeData();
    // Only re-sync checkboxes from the store when applied refinements change.
    // Facet-count refreshes also hit initializeData; restoring there would
    // overwrite in-flight toggles before setStockItemFilters updates the store.
    if (changes['appliedRefinementFilters']) {
      this.restoreAppliedRefinementFilters();
    }
  }

  initializeData(): void {
    if (this.selectedSearchableFields && Object.keys(this.selectedSearchableFields).length > 0) {
      this.searchableFields = getDeepCopyOfObject(this.selectedSearchableFields);
    }
    if (this.facetCounts && Object.keys(this.facetCounts).length > 0) {
      this.facets = getDeepCopyOfObject(this.facetCounts);
    }
    if (this.lineColours && Object.keys(this.lineColours).length > 0) {
      this.lineColourCopy = getDeepCopyOfObject(this.lineColours);
    }

    if (this.selectedSuppliers && Object.keys(this.selectedSuppliers).length > 0) {
      this.suppliers = getDeepCopyOfObject(this.selectedSuppliers);
      this.initFuzzySorts('supplier');
    }

    if (this.selectedDepartmentsAndSubDepartments && this.selectedDepartmentsAndSubDepartments.length > 0) {
      this.transformDep();
      this.initFuzzySorts('department');
      this.initFuzzySorts('subDepartment');
    }
    if (this.facetCounts && Object.keys(this.facetCounts).length > 0) {
      this.facets = getDeepCopyOfObject(this.facetCounts);
      if (this.facets['_tags']) {
        const previousTags = this.tags || {};
        this.tags = this.preserveSelectedKeys(
          Object.keys(this.facets['_tags']).reduce((acc: ISearchableFields, key) => {
            acc[key] = {
              name: key,
              value: previousTags[key]?.value ?? false,
            };
            return acc;
          }, {}),
          previousTags,
        );
      }
      if (this.facets['multipleSuppliers']) {
        const previousMultipleSuppliers = this.multipleSuppliers || {};
        this.multipleSuppliers = this.preserveSelectedKeys(
          Object.keys(this.facets['multipleSuppliers']).reduce((acc: ISearchableFields, key) => {
            acc[key] = {
              name: key,
              value: previousMultipleSuppliers[key]?.value ?? false,
            };
            return acc;
          }, {}),
          previousMultipleSuppliers,
        );
        this.initFuzzySorts('multipleSuppliers');
      }
    }
    this.ensureLineColourOptions();
    this.reapplyRefinementSearches();
  }

  /**
   * Facet refreshes rebuild the full option lists. Keep whatever the user typed
   * in a refinement search so ticking a result does not dump the whole list back.
   */
  private reapplyRefinementSearches(): void {
    if (this.searchSuppliers?.trim() && this.fuzzySuppliers) {
      this.filterSuppliers();
    }
    if (this.searchDepartments?.trim() && this.fuzzyDepartments) {
      this.filterDepartments();
    }
    if (this.searchSubDepartments?.trim() && this.fuzzySubDepartments) {
      this.filterSubDepartments();
    }
    if (this.searchMultipleSuppliers?.trim() && this.fuzzyMultipleSuppliers) {
      this.filterMultipleSuppliers();
    }
  }

  /**
   * Keeps line colour 0 in the list as "No line colour", even when the palette has no entry for it.
   */
  private ensureLineColourOptions(): void {
    if (this.hideLineColours || !isNonEmptyObject(this.lineColourCopy ?? this.lineColours)) {
      return;
    }
    const previousLineColourCodes = this.lineColourCodes || {};
    const next: ISearchableFields = {};
    Object.keys(this.facets?.['lineColourCode'] ?? {}).forEach((key: string) => {
      next[key] = {
        name: key === NO_LINE_COLOUR_KEY ? NO_LINE_COLOUR_LABEL : key,
        value: previousLineColourCodes[key]?.value ?? false,
      };
    });
    next[NO_LINE_COLOUR_KEY] = {
      name: NO_LINE_COLOUR_LABEL,
      value: previousLineColourCodes[NO_LINE_COLOUR_KEY]?.value ?? false,
    };
    this.lineColourCodes = this.preserveSelectedKeys(next, previousLineColourCodes);
  }

  protected lineColourOptionKeys(): string[] {
    return Object.keys(this.lineColourCodes ?? {}).sort((left: string, right: string) => {
      if (left === NO_LINE_COLOUR_KEY) {
        return -1;
      }
      if (right === NO_LINE_COLOUR_KEY) {
        return 1;
      }
      return left.localeCompare(right, undefined, {numeric: true});
    });
  }

  protected showsLineColour(key: string): boolean {
    if (key === NO_LINE_COLOUR_KEY) {
      return true;
    }
    return this.showsFacet('lineColourCode', key, !!this.lineColourCodes?.[key]?.value) && !!this.lineColours?.[key];
  }

  protected lineColourLabel(key: string): string {
    if (key === NO_LINE_COLOUR_KEY) {
      return `${NO_LINE_COLOUR_KEY} – ${NO_LINE_COLOUR_LABEL}`;
    }
    return `${key} – ${this.lineColourCopy?.[key]?.description ?? 'None'}`;
  }

  protected lineColourBackground(key: string): string | null {
    if (key === NO_LINE_COLOUR_KEY) {
      return null;
    }
    return this.lineColours?.[key]?.value ?? null;
  }

  protected chipText(key: string): string {
    const supplierName = this.suppliers?.[key]?.name?.trim() || this.filteredSuppliers?.[key]?.name?.trim();
    const chipName = this.chips?.[key]?.name?.trim();
    const name = supplierName || chipName;
    if (name && name !== key && !name.includes('::')) {
      return name;
    }
    const separator = key.indexOf('::');
    if (separator >= 0) {
      return key.slice(separator + 2);
    }
    return name || key;
  }

  protected chipTooltip(key: string): string {
    return `${this.chipText(key)}. Click to remove`;
  }

  protected activeRefinementCount(): number {
    const chipCount = this.chips ? Object.keys(this.chips).length : 0;
    const hideDisabledCount = this.hideDisabledItems ? 1 : 0;
    const savedFilterCount = this.selectedAdvancedFilter?.name ? 1 : 0;
    return chipCount + hideDisabledCount + savedFilterCount;
  }

  /**
   * Writes the checkbox first, then rebuilds the filter. The native change event
   * can run before ngModel updates, which was clearing the selection on refresh.
   */
  protected onRefinementValueChange(field: ISearchableField | undefined, checked: boolean): void {
    if (!field || field.value === checked) {
      return;
    }
    field.value = checked;
    this.updateFilters();
  }

  protected onSearchableFieldChange(field: ISearchableField | undefined, checked: boolean): void {
    if (!field || field.value === checked) {
      return;
    }
    field.value = checked;
    this.updateSearchableFields();
  }

  protected onClearAllRefinements(): void {
    this.clearAllValues(this.filteredSuppliers);
    this.clearAllValues(this.suppliers);
    this.clearAllValues(this.filteredDepartments);
    this.clearAllValues(this.departments);
    this.clearAllValues(this.filteredSubDepartments);
    this.clearAllValues(this.subDepartments);
    this.clearAllValues(this.tags);
    this.clearAllValues(this.lineColourCodes);
    this.clearAllValues(this.filteredMultipleSuppliers);
    this.clearAllValues(this.multipleSuppliers);
    this.hideDisabledItems = false;
    this.chips = {};
    const savedFilter = this.selectedAdvancedFilter;
    if (savedFilter) {
      this.onApplyAdvancedFiltersClick(savedFilter);
    }
    this.updateFilters();
  }

  protected onHideDisabledToggle(checked: boolean): void {
    if (this.hideDisabledItems === checked) {
      return;
    }
    this.hideDisabledItems = checked;
    this.updateFilters();
  }

  /**
   * True when a refinement row should stay visible. A selected value stays visible even when
   * the current facet count no longer includes it, so it can be cleared.
   */
  showsFacet(facetGroup: string, key: string, selected: boolean): boolean {
    return !!this.facets?.[facetGroup]?.[key] || selected;
  }

  hasSelected(fields: ISearchableFields | undefined): boolean {
    return !!fields && Object.values(fields).some((field) => field?.value === true);
  }

  initFuzzySorts(type: string): void {
    switch (type) {
      case 'department':
        const previousFilteredDepartments = this.filteredDepartments || {};
        this.fuzzyDepartments = new FuzzySort(
          Object.keys(this.departments).map((key: string) => ({department: key, ...this.departments[key]})),
          {
            keys: ['department', 'name'],
            threshold: -1000,
          },
        );
        this.filteredDepartments = this.mergeWithPreviousValues(previousFilteredDepartments, this.departments);
        break;

      case 'subDepartment':
        const previousFilteredSubDepartments = this.filteredSubDepartments || {};
        this.fuzzySubDepartments = new FuzzySort(
          Object.keys(this.subDepartments).map((key: string) => ({subDepartment: key, ...this.subDepartments[key]})),
          {
            keys: ['subDepartment', 'name'],
            threshold: -1000,
          },
        );
        this.filteredSubDepartments = this.mergeWithPreviousValues(previousFilteredSubDepartments, this.subDepartments);
        break;

      case 'supplier':
        if (!isNonEmptyObject(this.suppliers)) {
          break;
        }
        const previousFilteredSuppliers = this.filteredSuppliers || {};
        this.fuzzySuppliers = new FuzzySort(
          Object.keys(this.suppliers).map((key: string) => ({supplier: key, ...this.suppliers[key]})),
          {
            keys: ['supplier', 'name'],
            threshold: -1000,
          },
        );
        this.filteredSuppliers = this.mergeWithPreviousValues(previousFilteredSuppliers, this.suppliers);
        break;

      case 'multipleSuppliers':
        if (!isNonEmptyObject(this.suppliers)) {
          break;
        }
        const previousFilteredMultipleSuppliers = this.filteredMultipleSuppliers || {};
        this.fuzzyMultipleSuppliers = new FuzzySort(
          Object.keys(this.facets['multipleSuppliers']).map((key: string) => ({supplier: key, ...this.suppliers[key]})),
          {
            keys: ['supplier', 'name'],
            threshold: -1000,
          },
        );
        this.filteredMultipleSuppliers = this.mergeWithPreviousValues(previousFilteredMultipleSuppliers, this.multipleSuppliers);
        break;

      default:
        break;
    }
  }


  doFieldExist(fields: ISearchableFields): boolean {
    return Object.keys(fields).length > 0;
  }

  onAccordionChange(isNowOpen: boolean, title: string): void {
    if (isNowOpen) {
      this.openAccordion = title;
    } else {
      this.openAccordion = null;
    }
  }

  transformDep(): void {
    this.searchableDepartments = this.selectedDepartmentsAndSubDepartments
      .reduce((acc: ISearchableFields, department: IDepartment) => {
        acc[department.department] = {
          name: department.name,
          value: false,
        };
        return acc;
      }, {} as ISearchableFields);
    this.departments = getDeepCopyOfObject(this.searchableDepartments);
    this.searchableSubDepartments = this.selectedDepartmentsAndSubDepartments
      .reduce((acc: ISearchableFields, department: IDepartment) => {
        department.subDepartments.forEach((subDep: ISubDepartment): void => {
          acc[subDep.subDepartment] = {
            name: subDep.name,
            value: false,
          };
        });
        return acc;
      }, {} as ISearchableFields);
    this.subDepartments = getDeepCopyOfObject(this.searchableSubDepartments);
  }

  filterSuppliers(): void {
    const previousFilteredSuppliers = this.filteredSuppliers || {};
    if (this.searchSuppliers.trim() === '') {
      this.filteredSuppliers = this.mergeWithPreviousValues(previousFilteredSuppliers, this.suppliers);
    } else {
      const filteredResults = this.getFuzzySearchResults(this.fuzzySuppliers, this.searchSuppliers, this.suppliers, 'supplier');
      this.filteredSuppliers = this.mergeWithPreviousValues(previousFilteredSuppliers, filteredResults);
    }
  }

  filterMultipleSuppliers(): void {
    const previousFilteredMultipleSuppliers = this.filteredMultipleSuppliers || {};
    if (this.searchMultipleSuppliers.trim() === '') {
      this.filteredMultipleSuppliers = this.mergeWithPreviousValues(previousFilteredMultipleSuppliers, this.multipleSuppliers);
    } else {
      // FIX: Change the propertyName from 'multipleSuppliers' to 'supplier'
      const filteredResults = this.getFuzzySearchResults(this.fuzzyMultipleSuppliers, this.searchMultipleSuppliers, this.multipleSuppliers, 'supplier');
      this.filteredMultipleSuppliers = this.mergeWithPreviousValues(previousFilteredMultipleSuppliers, filteredResults);
    }
  }

  filterDepartments(): void {
    const previousFilteredDepartments = this.filteredDepartments || {};
    if (this.searchDepartments.trim() === '') {
      this.filteredDepartments = this.mergeWithPreviousValues(previousFilteredDepartments, this.departments);
    } else {
      const filteredResults = this.getFuzzySearchResults(this.fuzzyDepartments, this.searchDepartments, this.departments, 'department');
      this.filteredDepartments = this.mergeWithPreviousValues(previousFilteredDepartments, filteredResults);
    }
  }

  filterSubDepartments(): void {
    const previousFilteredSubDepartments = this.filteredSubDepartments || {};
    if (this.searchSubDepartments.trim() === '') {
      this.filteredSubDepartments = this.mergeWithPreviousValues(previousFilteredSubDepartments, this.subDepartments);
    } else {
      const filteredResults = this.getFuzzySearchResults(this.fuzzySubDepartments, this.searchSubDepartments, this.subDepartments, 'subDepartment');
      this.filteredSubDepartments = this.mergeWithPreviousValues(previousFilteredSubDepartments, filteredResults);
    }
  }

  updateSearchableFields(): void {
    if (this.isStockManager) {
      this.store.dispatch(setSearchableFields({searchableFields: this.searchableFields}));
    } else {
      this.emitSearchableFields.emit(this.searchableFields);
    }
    if (this.filteredDepartments) {
      this.updateFilters();
    }
  }

  updateFilters(): void {
    const appliedFilters: Record<string, ISearchableFields> = {};
    const filteredSuppliers = this.getSelectedFilters(this.filteredSuppliers);
    const filteredMultipleSuppliers = this.getSelectedFilters(this.filteredMultipleSuppliers);
    const filteredDep = this.getSelectedFilters(this.filteredDepartments);
    const filteredSubDep = this.getSelectedFilters(this.filteredSubDepartments);
    const filteredTags = this.getSelectedFilters(this.tags);
    const filteredLineColourCodes = this.getSelectedFilters(this.lineColourCodes);

    if (Object.keys(filteredSuppliers).length > 0) {
      appliedFilters['40'] = filteredSuppliers;
    }
    if (Object.keys(filteredDep).length > 0) {
      appliedFilters['3'] = filteredDep;
    }
    if (Object.keys(filteredSubDep).length > 0) {
      appliedFilters['4'] = filteredSubDep;
    }
    if (Object.keys(filteredTags).length > 0) {
      appliedFilters['_tags'] = filteredTags;
    }
    if (Object.keys(filteredLineColourCodes).length > 0) {
      appliedFilters['46'] = filteredLineColourCodes;
    }
    if (Object.keys(filteredMultipleSuppliers).length > 0) {
      appliedFilters['multipleSuppliers'] = filteredMultipleSuppliers;
    }
    if (this.hideDisabledItems) {
      appliedFilters[STOCK_REFINEMENT_HIDE_DISABLED_FIELD] = {
        disabled: {name: 'Hide disabled', value: true},
      };
    }
    this.chips = {...filteredSuppliers, ...filteredDep, ...filteredSubDep, ...filteredTags, ...filteredLineColourCodes, ...filteredMultipleSuppliers};
    const filtersToApply = Object.keys(appliedFilters).length > 0 ? appliedFilters : {};
    if (this.isStockManager) {
      this.store.dispatch(setStockItemFilters({filters: filtersToApply}));
    } else {
      this.emitFilters.emit(filtersToApply);
    }
  }

  onRemoveFilterClick(key: string): void {
    delete this.chips[key];
    this.clearSelectedValue(this.filteredSuppliers, key);
    this.clearSelectedValue(this.filteredDepartments, key);
    this.clearSelectedValue(this.filteredSubDepartments, key);
    this.clearSelectedValue(this.tags, key);
    this.clearSelectedValue(this.filteredMultipleSuppliers, key);
    this.clearSelectedValue(this.lineColourCodes, key);
    this.updateFilters();
  }

  getSelectedFilters(fields: ISearchableFields): ISearchableFields {
    if (isNonEmptyObject(fields)) {
      return Object.entries(fields).reduce((acc: ISearchableFields, [key, filter]) => {
        if (filter?.value) {
          acc[key] = filter;
        }
        return acc;
      }, {} as ISearchableFields);
    }

    return {} as ISearchableFields;
  }

  getFuzzySearchResults(
    fuzzy: FuzzySort,
    searchable: string,
    original: ISearchableFields,
    propertyName: 'department' | 'supplier' | 'subDepartment' | 'multipleSuppliers',
  ): ISearchableFields {
    const results: ObjResult<unknown>[] = fuzzy.search(searchable) as ObjResult<unknown>[];
    return results.reduce<ISearchableFields>((acc: ISearchableFields, result: ObjResult<unknown>) => {
      const key = result.obj[propertyName];
      acc[key] = original[key];
      return acc;
    }, {} as ISearchableFields);
  }

  onApplyAdvancedFiltersClick(filter: IAdvancedFilterGroup): void {
    if (this.selectedFilterName === filter.name) {
      if (this.isStockManager) {
        this.selectedAdvancedFilter = null;
        this.store.dispatch(setSelectedAdvancedFilterGroup({advancedFilterGroup: filter}));
        this.store.dispatch(removeAdvancedFilterGroupFilterFromGrid());
      } else {
        this.emitFilterGroup.emit({advancedFilterGroup: filter, type: 'remove'});
        this.emitFilters.emit({});
      }

    } else {
      if (this.isStockManager) {
        this.selectedAdvancedFilter = filter;
        this.store.dispatch(setSelectedAdvancedFilterGroup({advancedFilterGroup: filter}));
        this.store.dispatch(applyAdvancedFilterGroupFilterToGrid({advancedFilterGroup: filter}));
      } else {
        this.emitFilterGroup.emit({advancedFilterGroup: filter, type: 'add'});
        this.emitFilters.emit({});
      }

    }
  }

  /**
   * Puts the store's selected refinements back onto the checkboxes and chips.
   * The panel is recreated on each visit, so without this the selection is still
   * filtering the grid with no control left to clear it.
   */
  private restoreAppliedRefinementFilters(): void {
    if (!this.isStockManager) {
      return;
    }
    const filters = this.storedRefinementFilters;
    this.hideDisabledItems = isHideDisabledRefinement(filters);
    this.filteredSuppliers = this.withStoredSelection(this.filteredSuppliers, filters['40']);
    this.suppliers = this.withStoredSelection(this.suppliers, filters['40']);
    this.filteredDepartments = this.withStoredSelection(this.filteredDepartments, filters['3']);
    this.departments = this.withStoredSelection(this.departments, filters['3']);
    this.filteredSubDepartments = this.withStoredSelection(this.filteredSubDepartments, filters['4']);
    this.subDepartments = this.withStoredSelection(this.subDepartments, filters['4']);
    this.tags = this.withStoredSelection(this.tags, filters['_tags']);
    this.lineColourCodes = this.withStoredSelection(this.lineColourCodes, filters['46']);
    this.filteredMultipleSuppliers = this.withStoredSelection(
      this.filteredMultipleSuppliers,
      filters['multipleSuppliers'],
    );
    this.multipleSuppliers = this.withStoredSelection(this.multipleSuppliers, filters['multipleSuppliers']);
    this.chips = {
      ...(filters['40'] ?? {}),
      ...(filters['3'] ?? {}),
      ...(filters['4'] ?? {}),
      ...(filters['_tags'] ?? {}),
      ...(filters['46'] ?? {}),
      ...(filters['multipleSuppliers'] ?? {}),
    };
  }

  private withStoredSelection(
    current: ISearchableFields | undefined,
    selected: ISearchableFields | undefined,
  ): ISearchableFields | undefined {
    if (!current && !isNonEmptyObject(selected)) {
      return current;
    }
    const next: ISearchableFields = {...(current ?? {})};
    Object.keys(next).forEach((key: string) => {
      next[key] = {...next[key], value: !!selected?.[key]?.value};
    });
    Object.entries(selected ?? {}).forEach(([key, field]) => {
      if (field?.value) {
        next[key] = {...(next[key] ?? field), value: true};
      }
    });
    return next;
  }

  /**
   * Keeps currently selected keys on the map when facet options are rebuilt, so a
   * selected value that dropped out of facet counts remains visible and clearable.
   */
  private preserveSelectedKeys(
    next: ISearchableFields,
    previous: ISearchableFields | undefined,
  ): ISearchableFields {
    const result: ISearchableFields = {...next};
    Object.entries(previous ?? {}).forEach(([key, field]) => {
      if (field?.value) {
        result[key] = {...(result[key] ?? field), value: true};
      }
    });
    return result;
  }

  private clearAllValues(fields: ISearchableFields | undefined): void {
    if (!fields) {
      return;
    }
    Object.keys(fields).forEach((key: string) => {
      if (fields[key]) {
        fields[key].value = false;
      }
    });
  }

  private clearSelectedValue(fields: ISearchableFields | undefined, key: string): void {
    if (fields?.[key]) {
      fields[key].value = false;
    }
  }

  private mergeWithPreviousValues(
    previous: ISearchableFields,
    current: ISearchableFields,
  ): ISearchableFields {
    return Object.entries(current).reduce((acc: ISearchableFields, [key, value]) => {
      acc[key] = {
        ...value,
        value: previous?.[key]?.value ?? value.value,
      };
      return acc;
    }, {} as ISearchableFields);
  }
}

//never forget about Bailey Hassall, he was the best of us